import { ShieldAlert } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { Chip } from '@/components/redesign/bits'
import { Dialog, DialogClose, DialogContent } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { errorMessage, isApiError } from '@/lib/api/errors'
import type { CustomerDetail, CustomerSafety, DuplicateReason } from '@/lib/api/types'
import { formatDate } from '@/lib/format'
import { useLiftFreeLimit, useLiftNetwork, useLimitFree, useLimitNetwork } from './api'

const REASONS: Record<DuplicateReason, string> = {
  mailbox: 'Same inbox',
  website: 'Same website',
  network: 'Shared network near a sign-up (within 24 hours)',
}

const LIMITED =
  'Keyword research and data, backlink, competitor, map grid and Business Profile lookups, rank checks and Arva SEO AI credits pause on Free. Everything else keeps working and nothing is deleted. The owner is told by email and in the app.'

type Target = { kind: 'workspace' } | { kind: 'network'; network: CustomerSafety['networks'][number] }

/**
 * Free plan safeguards (owner decision 2026-10-21): workspaces that may be the same person's, the
 * networks the owner used (as hashes; addresses are never kept), and limiting Free features, for
 * this workspace or a whole network, never banning. Owners and admins can limit and lift.
 */
export function SafetyTab({ customer: c, canManage }: { customer: CustomerDetail; canManage: boolean }) {
  const [target, setTarget] = useState<Target | null>(null)
  const lift = useLiftFreeLimit(c.id)
  const liftNetwork = useLiftNetwork(c.id)
  const safety = c.safety
  if (!safety) return null

  return (
    <div className="grid gap-5">
      <Card>
        <CardHeader
          title="Free features"
          description={
            safety.free
              ? 'Limit Free features when this looks like a second Free account. It never bans anyone.'
              : 'This workspace isn’t on Free, so nothing is limited.'
          }
          actions={
            canManage &&
            safety.free &&
            c.closed_at === null &&
            (safety.limited ? (
              <Button
                size="sm"
                variant="secondary"
                loading={lift.isPending}
                onClick={() =>
                  lift.mutate(undefined, {
                    onSuccess: () => toast.success('Limit lifted. The owner is told.'),
                    onError: (e) => toast.error(errorMessage(e)),
                  })
                }
              >
                Lift the limit
              </Button>
            ) : (
              <Button size="sm" variant="secondary" onClick={() => setTarget({ kind: 'workspace' })}>
                <ShieldAlert /> Limit Free features
              </Button>
            ))
          }
        />
        <CardBody className="grid gap-2 text-[13px]">
          {safety.limited ? (
            <>
              <p className="flex flex-wrap items-center gap-2">
                <Chip tone="warn">Limited</Chip>
                <span className="text-ink-muted">
                  Since {formatDate(safety.limited.at)}
                  {safety.limited.network ? ', for its network' : ''}
                  {safety.limited.by ? `, by ${safety.limited.by}` : ''}.
                </span>
              </p>
              {safety.limited.note && <p className="text-ink-muted">Reason: {safety.limited.note}</p>}
            </>
          ) : (
            <p className="text-ink-muted">Not limited.</p>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Possible duplicates"
          description="Other customer workspaces that look like the same person. Check before limiting: offices and agencies share networks."
        />
        <CardBody>
          {safety.duplicates.length === 0 ? (
            <p className="text-[13px] text-ink-subtle">None found.</p>
          ) : (
            <ul className="grid divide-y divide-border text-[13px]" aria-label="Possible duplicates">
              {safety.duplicates.map((d) => (
                <li key={d.workspace_id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span className="grid gap-0.5">
                    <Link
                      to={`/customers/${d.workspace_id}`}
                      className="font-medium text-ink underline-offset-2 hover:underline"
                    >
                      {d.name}
                    </Link>
                    <span className="text-xs text-ink-subtle">
                      {[d.owner, d.free ? 'Free' : 'Paid'].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  <span className="flex flex-wrap gap-1.5">
                    {d.reasons.map((r) => (
                      <Chip key={r} tone="warn" className="h-5 text-[11px]">
                        {REASONS[r]}
                      </Chip>
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Networks"
          description="Where the owner signed up or in over the last 90 days. Only a one-way code of each address is kept, so the addresses themselves aren’t shown."
        />
        <CardBody>
          {safety.networks.length === 0 ? (
            <p className="text-[13px] text-ink-subtle">None recorded yet.</p>
          ) : (
            <ul className="grid divide-y divide-border text-[13px]" aria-label="Networks">
              {safety.networks.map((n) => (
                <li key={n.hash} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span className="grid gap-0.5">
                    <span className="font-medium text-ink">
                      {n.label}
                      {n.signed_up && <span className="font-normal text-ink-subtle"> · signed up here</span>}
                    </span>
                    <span className="text-xs text-ink-subtle">
                      {n.accounts} {n.accounts === 1 ? 'account' : 'accounts'} · {n.free_workspaces} Free{' '}
                      {n.free_workspaces === 1 ? 'workspace' : 'workspaces'} · last seen{' '}
                      {formatDate(n.last_seen_at)}
                    </span>
                  </span>
                  {n.limit ? (
                    <span className="flex items-center gap-2">
                      <Chip tone="warn" className="h-5 text-[11px]">
                        Limited
                      </Chip>
                      {canManage && (
                        <Button
                          size="sm"
                          variant="ghost"
                          loading={liftNetwork.isPending}
                          onClick={() =>
                            liftNetwork.mutate(n.limit?.id ?? '', {
                              onSuccess: (r) =>
                                toast.success(
                                  `Network limit lifted: ${r.meta.lifted} ${r.meta.lifted === 1 ? 'workspace' : 'workspaces'} back to normal.`,
                                ),
                              onError: (e) => toast.error(errorMessage(e)),
                            })
                          }
                        >
                          Lift
                        </Button>
                      )}
                    </span>
                  ) : (
                    canManage && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setTarget({ kind: 'network', network: n })}
                      >
                        Limit this network
                      </Button>
                    )
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      {target && <LimitDialog customer={c} target={target} onClose={() => setTarget(null)} />}
    </div>
  )
}

function LimitDialog({
  customer: c,
  target,
  onClose,
}: {
  customer: CustomerDetail
  target: Target
  onClose: () => void
}) {
  const [note, setNote] = useState('')
  const one = useLimitFree(c.id)
  const network = useLimitNetwork(c.id)
  const pending = one.isPending || network.isPending
  const error = target.kind === 'workspace' ? one.error : network.error
  const fieldError = isApiError(error) ? error.firstFieldError('note') : undefined
  const count = target.kind === 'network' ? target.network.free_workspaces : 1

  const submit = () => {
    if (target.kind === 'workspace') {
      one.mutate(
        { note: note.trim() },
        {
          onSuccess: () => {
            toast.success('Free features limited. The owner is told.')
            onClose()
          },
          onError: (e) => toast.error(errorMessage(e)),
        },
      )
      return
    }
    network.mutate(
      { hash: target.network.hash, note: note.trim() },
      {
        onSuccess: (r) => {
          toast.success(
            `${target.network.label} limited: ${r.meta.limited} ${r.meta.limited === 1 ? 'workspace' : 'workspaces'} now, and any that sign up or in from it.`,
          )
          onClose()
        },
        onError: (e) => toast.error(errorMessage(e)),
      },
    )
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={
          target.kind === 'workspace' ? `Limit Free features on ${c.name}?` : `Limit ${target.network.label}?`
        }
        description={
          target.kind === 'workspace'
            ? LIMITED
            : `Every Free workspace whose owner used this network in the last 90 days (${count} now), and any that sign up or in from it later. ${LIMITED}`
        }
        footer={
          <>
            <DialogClose asChild>
              <Button variant="ghost">Cancel</Button>
            </DialogClose>
            <Button variant="danger" loading={pending} disabled={note.trim() === ''} onClick={submit}>
              {target.kind === 'workspace' ? 'Limit and tell the owner' : 'Limit the network'}
            </Button>
          </>
        }
      >
        <Field
          label="Reason"
          htmlFor="limit-note"
          required
          error={fieldError}
          hint="Shown to staff, not the customer."
        >
          <Input id="limit-note" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </DialogContent>
    </Dialog>
  )
}
